import {
  HSE_REPORT_TYPES,
  type HseqStage,
  type HseReportType,
  type HseStatus,
  hseqStageOf,
  hseTypeCode,
} from '@felix/domain';
import type { HseSummary } from './types.js';

/**
 * HSEQ Reports (ortak 2026-10-20b H1; ADR 0097 §1): dört tür tek listede. Menüde alt grup (ortak
 * 2026-10-25; ADR 0097 Ek): All Reports ve türler. Türün yolu aynı listeyi o türle süzülü açar;
 * kayıt ve yeni rapor o türle çalışır.
 */
export const HSEQ_PATH = '/compliance/hseq';
export const HSEQ_MENU_ID = 'complianceHseq';

/** HSEQ Reports alt grubunun dört tür öğesi (ADR 0049 §11, ADR 0097 §1 ve Ek). */
export const HSE_PATHS: Readonly<Record<HseReportType, string>> = {
  deficiency: '/compliance/deficiency',
  non_conformity: '/compliance/non-conformity',
  accident: '/compliance/accident',
  near_miss: '/compliance/near-miss',
};

export const HSE_MENU_IDS: Readonly<Record<HseReportType, string>> = {
  deficiency: 'complianceDeficiency',
  non_conformity: 'complianceNonConformity',
  accident: 'complianceAccident',
  near_miss: 'complianceNearMiss',
};

export type HseRoute =
  | { view: 'list' }
  | {
      view: 'new';
      parentId: string | null;
      requestId: string | null;
      /** HSEQ listesinden yeni raporun türü (`?type=`); türün yolunda yol belirler. */
      type?: HseReportType | null;
      /** Arızadan açılan rapor (`?defect=`): arıza bağlı gelir (ADR 0097 §7). */
      defectId?: string | null;
      /** "Raporu Kopyala" (`?copy=`): kaynak raporun alanlarıyla yeni taslak. */
      copyId?: string | null;
    }
  | { view: 'detail'; id: string; tab: string | null }
  // Ofisin şirket uygunsuzluğu (ADR 0062 §2): Non Conformity ekranında "Company" görünümü
  | { view: 'company' }
  | { view: 'companyNew' }
  | { view: 'companyDetail'; id: string };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TAB = /^[a-z_]{2,20}$/;

/** `?type=` değeri: tür adı ya da kısaltması (NC / DF / NM / ACC). */
export function hseTypeParam(value: string | null): HseReportType | null {
  if (!value) return null;
  const found = HSE_REPORT_TYPES.find(
    (type) => type === value || hseTypeCode(type).toLowerCase() === value.toLowerCase(),
  );
  return found ?? null;
}

/**
 * Ekranın görünümü sorgu dizesinden (`?new=1&parent=…&request=…&type=…&defect=…&copy=…`,
 * `?id=…&tab=…`).
 */
export function hseRoute(search: string): HseRoute {
  const params = new URLSearchParams(search);
  const company = params.get('company');
  if (company === '1') return { view: 'company' };
  if (company === 'new') return { view: 'companyNew' };
  if (company && UUID.test(company)) return { view: 'companyDetail', id: company };
  const id = params.get('id');
  if (id && UUID.test(id)) {
    const tab = params.get('tab');
    return { view: 'detail', id, tab: tab && TAB.test(tab) ? tab : null };
  }
  if (params.get('new') === '1') {
    const parent = params.get('parent');
    const request = params.get('request');
    const defect = params.get('defect');
    const copy = params.get('copy');
    const type = hseTypeParam(params.get('type'));
    return {
      view: 'new',
      parentId: parent && UUID.test(parent) ? parent : null,
      requestId: request && UUID.test(request) ? request : null,
      ...(type ? { type } : {}),
      ...(defect && UUID.test(defect) ? { defectId: defect } : {}),
      ...(copy && UUID.test(copy) ? { copyId: copy } : {}),
    };
  }
  return { view: 'list' };
}

/** Yol: tür verilirse türün yolu, verilmezse HSEQ Reports. */
export function hsePath(type: HseReportType | null, route: HseRoute): string {
  const base = type ? HSE_PATHS[type] : HSEQ_PATH;
  switch (route.view) {
    case 'list':
      return base;
    case 'new': {
      const params = new URLSearchParams({ new: '1' });
      if (route.parentId) params.set('parent', route.parentId);
      if (route.requestId) params.set('request', route.requestId);
      if (route.type && !type) params.set('type', route.type);
      if (route.defectId) params.set('defect', route.defectId);
      if (route.copyId) params.set('copy', route.copyId);
      return `${base}?${params}`;
    }
    case 'detail':
      return `${base}?id=${route.id}${route.tab ? `&tab=${route.tab}` : ''}`;
    case 'company':
      return `${base}?company=1`;
    case 'companyNew':
      return `${base}?company=new`;
    case 'companyDetail':
      return `${base}?company=${route.id}`;
  }
}

export interface HseFilter {
  /** Tür süzgeci (NC / DF / NM / ACC); boş: hepsi. */
  types: readonly HseReportType[];
  /** Dört ana aşama (ADR 0097 §3). */
  stage: HseqStage | null;
  search: string;
  status: HseStatus | 'open' | 'overdue' | 'pending_review' | null;
  priority: string | null;
  vesselId: string | null;
  /** Olay günü aralığı (`YYYY-MM-DD`, ikisi de dahil). */
  from: string | null;
  to: string | null;
}

export const NO_HSE_FILTER: HseFilter = {
  types: [],
  stage: null,
  search: '',
  status: null,
  priority: null,
  vesselId: null,
  from: null,
  to: null,
};

const OPEN: readonly HseStatus[] = [
  'submitted',
  'under_review',
  'action_assigned',
  'in_progress',
  'pending_verification',
  'reopened',
];

/** Süzgeç: arama numarada, başlıkta, açıklamada, konumda ve sorumluda; kart süzgeçleri durumda. */
export function filterHse(items: readonly HseSummary[], filter: HseFilter): HseSummary[] {
  const words = filter.search.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  return items.filter((item) => {
    if (filter.types.length > 0 && !filter.types.includes(item.reportType)) return false;
    if (filter.stage && hseqStageOf(item.status) !== filter.stage) return false;
    if (filter.vesselId && item.vesselId !== filter.vesselId) return false;
    if (filter.priority && item.priority !== filter.priority) return false;
    if (filter.status === 'open' && !OPEN.includes(item.status)) return false;
    if (filter.status === 'overdue' && !item.overdue) return false;
    if (filter.status === 'pending_review' && !item.awaitingReview) return false;
    if (
      filter.status &&
      filter.status !== 'open' &&
      filter.status !== 'overdue' &&
      filter.status !== 'pending_review' &&
      item.status !== filter.status
    ) {
      return false;
    }
    const day = item.localDate ?? item.createdAt.slice(0, 10);
    if (filter.from && day < filter.from) return false;
    if (filter.to && day > filter.to) return false;
    if (words.length === 0) return true;
    const text = [
      item.reportNo,
      item.title,
      item.description,
      item.location,
      item.responsible,
      item.vesselName,
    ]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase();
    return words.every((word) => text.includes(word));
  });
}

export type HseColumn =
  | 'reportNo'
  | 'type'
  | 'date'
  | 'vessel'
  | 'category'
  | 'description'
  | 'status'
  | 'priority'
  | 'responsible';

const PRIORITY_RANK: Readonly<Record<string, number>> = { low: 1, medium: 2, high: 3, critical: 4 };

export const HSE_SORT_VALUE: Readonly<Record<HseColumn, (item: HseSummary) => string | number>> = {
  reportNo: (item) => item.reportNo,
  type: (item) => hseTypeCode(item.reportType),
  date: (item) => item.occurredAt ?? item.createdAt,
  vessel: (item) => item.vesselName,
  category: (item) => item.category ?? '',
  description: (item) => item.title,
  status: (item) => OPEN.indexOf(item.status),
  priority: (item) => PRIORITY_RANK[item.priority ?? ''] ?? 0,
  responsible: (item) => item.responsible ?? '',
};

export function sortHse(
  items: readonly HseSummary[],
  column: HseColumn,
  dir: 'asc' | 'desc',
): HseSummary[] {
  const value = HSE_SORT_VALUE[column];
  const sign = dir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    const x = value(a);
    const y = value(b);
    const order =
      typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
    return order * sign || b.reportNo.localeCompare(a.reportNo);
  });
}

/** Dört aşamanın rozet tonu (ADR 0097 §3). */
export function hseqStageTone(stage: HseqStage): 'success' | 'warning' | 'danger' | 'info' {
  switch (stage) {
    case 'reported':
      return 'danger';
    case 'under_action':
      return 'warning';
    case 'verification':
      return 'info';
    case 'closed':
      return 'success';
  }
}

/** Durumun rozet tonu (BGM: Open kırmızı, In Progress turuncu, Closed yeşil). */
export function hseStatusTone(
  status: HseStatus,
): 'success' | 'warning' | 'danger' | 'info' | 'neutral' {
  switch (status) {
    case 'closed':
      return 'success';
    case 'submitted':
    case 'reopened':
      return 'danger';
    case 'in_progress':
    case 'action_assigned':
      return 'warning';
    case 'under_review':
    case 'pending_verification':
      return 'info';
    default:
      return 'neutral';
  }
}

export function hsePriorityTone(
  priority: string | null,
): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (priority) {
    case 'critical':
    case 'high':
      return 'danger';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'neutral';
  }
}
